/* eslint-disable @typescript-eslint/no-explicit-any */
import { CSSProperties } from 'react';
import { Column } from '@tanstack/react-table';
import { TableRow } from '../../../stores/table-store';

type PinningResult = { styles: CSSProperties; classNames: string };

// Per-column memo cache. getCommonPinningStyles is called for every rendered
// cell (and header cell) on every commit; the result only changes when the
// column's size or its pinned start/after offset changes (resize / reorder /
// pin toggle), which is rare. Caching keyed on those inputs keeps the returned
// `styles` object identity stable between renders, so React can skip
// reconciling the `style` prop, and skips the repeated getStart/getAfter/
// getSize work. WeakMap keying lets entries be GC'd with their column.
const pinningCache = new WeakMap<
  Column<TableRow, any>,
  { key: string; result: PinningResult }
>();

export const getCommonPinningStyles = (
  column: Column<TableRow, any>
): PinningResult => {
  const isPinned = column.getIsPinned();
  const size = column.getSize();
  const start = isPinned === 'left' ? column.getStart('left') : 0;
  const after = isPinned === 'right' ? column.getAfter('right') : 0;
  const isLastLeftPinnedColumn =
    isPinned === 'left' && column.getIsLastColumn('left');
  const isFirstRightPinnedColumn =
    isPinned === 'right' && column.getIsFirstColumn('right');

  // Cache key captures every input that affects the output.
  const cacheKey = `${isPinned ?? ''}:${size}:${start}:${after}:${isLastLeftPinnedColumn ? 1 : 0}:${isFirstRightPinnedColumn ? 1 : 0}`;
  const cached = pinningCache.get(column);
  if (cached && cached.key === cacheKey) {
    return cached.result;
  }

  const styles = {
    left: isPinned === 'left' ? `${start}px` : undefined,
    right: isPinned === 'right' ? `${after}px` : undefined,
    minWidth: `var(--col-${column.id}-size, ${size}px)`,
    maxWidth: `var(--col-${column.id}-size, ${size}px)`,
  } as CSSProperties;

  const classNames = `${isPinned ? 'c-editor-table-pinned-cell' : ''} ${isLastLeftPinnedColumn ? 'c-editor-table-pinned-cell--last-left' : ''} ${isFirstRightPinnedColumn ? 'c-editor-table-pinned-cell--first-right' : ''}`;

  const result: PinningResult = { styles, classNames };
  pinningCache.set(column, { key: cacheKey, result });
  return result;
};
